import type { OvpClient } from '@bldsoft/cappie/api/ovp'
import type { API, TvShowEpisode } from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'
import { payload } from '../../result'
import { withTotal } from '../../utils/paginatedList'
import { format, formatEpisode, formatGenre, formatSeason } from './format'
import type {
  ApiTvShow,
  ApiTvShowEpisode,
  ApiTvShowSeason,
  ApiTvShowsGenre,
  ApiTvShowsGenres,
  ApiTvShowsGenresToLanguages,
  ApiTvShowsLanguage,
  ApiTvShowsLanguages,
} from './index.h'

export function use(
  client: OvpClient,
  tvshows: API['tvshows'],
  _api: API,
  _defaults: AdapterDefaults
): void {
  // GET /api/web/tvshows/genres
  tvshows.base.getTvShowsGenresFx.use(async ({ withDescriptions }) => {
    const data = payload<{ results: ApiTvShowsGenre[]; total: number }>(
      await client.get.api.web.tvshows.genres({
        query: { withDescriptions },
      })
    )

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getTvShowsGenresFx')
    }

    const results = (data.results || []).map(formatGenre)

    return withTotal(results, data.total) as ApiTvShowsGenres
  })

  // GET /api/web/tvshows/languages
  tvshows.base.getTvShowsLanguagesFx.use(async ({ withDescriptions }) => {
    const data = payload<{ results: ApiTvShowsLanguage[]; total: number }>(
      await client.get.api.web.tvshows.languages({
        query: { withDescriptions },
      })
    )

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getTvShowsLanguagesFx')
    }

    const results = (data.results || []).map(formatGenre)

    return withTotal(results, data.total) as ApiTvShowsLanguages
  })

  // GET /api/web/tvshows/genres-to-languages
  tvshows.base.getTvShowsGenresToLanguagesFx.use(async () => {
    const data = payload<ApiTvShowsGenresToLanguages>(
      await client.get.api.web.tvshows.genresToLanguages()
    )

    if (!data) {
      throw new Error(
        'Empty answer in tvshows.base.getTvShowsGenresToLanguagesFx'
      )
    }

    return data || []
  })

  // GET /api/web/tvshows/by-genre/{genreId}
  tvshows.base.getTvShowsByGenreFx.use(
    async ({ id, limit, offset, languageId }) => {
      const data = payload<{ results: ApiTvShow[]; total: number }>(
        await client.get.api.web.tvshows.byGenre(id, {
          query: { limit, offset, languageId },
        })
      )

      if (!data) {
        throw new Error('Empty answer in tvshows.base.getTvShowsByGenreFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/tvshows/${id}
  tvshows.base.getTvShowFx.use(async ({ id }) => {
    const data = payload<ApiTvShow>(await client.get.api.web.tvshows(id)())

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getTvShowFx')
    }

    return format(data)
  })

  // GET /api/web/tvshows/${id}/seasons
  tvshows.base.getTvShowSeasonsByIdFx.use(
    async ({ id, withEpisodeCounts = false }) => {
      const data = payload<ApiTvShowSeason[]>(
        await client.get.api.web.tvshows(id).seasons({
          query: { withEpisodeCounts },
        })
      )

      if (!data) {
        throw new Error('Empty answer in tvshows.base.getTvShowSeasonsByIdFx')
      }

      return (data || []).map(formatSeason)
    }
  )

  // GET /api/web/tvshows/{id}/seasons/{seasonId}/episodes
  tvshows.base.getTvShowEpisodesBySeasonIdFx.use(
    async ({ id, seasonId, limit, offset }) => {
      const data = payload<{
        results: ApiTvShowEpisode[]
        total: number
      }>(
        await client.get.api.web.tvshows(id).seasons(seasonId).episodes({
          query: { limit, offset },
        })
      )

      if (!data) {
        throw new Error(
          'Empty answer in tvshows.base.getTvShowEpisodesBySeasonIdFx'
        )
      }

      const results = (data.results || []).map(formatEpisode)

      return withTotal(results, data.total) as TvShowEpisode[] & {
        total: number
      }
    }
  )

  // GET /api/web/tvshows/{id}/seasons/{seasonId}/episodes/{episodeId}/url
  tvshows.base.getOneTvShowEpisodeUrlPlaybackFx.use(
    async ({ id, seasonId, episodeId, headers = {} }) => {
      const data = payload<{ url: string }>(
        await client.get.api.web
          .tvshows(id)
          .seasons(seasonId)
          .episodes(episodeId)
          .url({
            headers: Object.keys(headers).length ? headers : undefined,
          })
      )

      if (!data) {
        throw new Error(
          'Empty answer in tvshows.base.getOneTvShowEpisodeUrlPlaybackFx'
        )
      }

      return data
    }
  )

  // GET /api/web/tvshows/by-language/{languageId}
  tvshows.base.getTvShowsByCollectionFx.use(async ({ id, limit, offset }) => {
    const data = payload<{ results: ApiTvShow[]; total: number }>(
      await client.get.api.web.tvshows.byLanguage(id, {
        query: { limit, offset },
      })
    )

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getTvShowsByCollectionFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })

  // GET /api/web/tvshows/by-hashtag/{hashtagId}
  tvshows.base.getTvShowsByHashtagFx.use(async ({ limit, offset, id }) => {
    const data = payload<{ results: ApiTvShow[]; total: number }>(
      await client.get.api.web.tvshows.byHashtag(id, {
        query: { limit, offset },
      })
    )

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getTvShowsByHashtagFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })

  // GET /api/web/tvshows/my-list
  tvshows.base.getFavoritesFx.use(async ({ limit, offset }) => {
    const data = payload<{ results: ApiTvShow[]; total: number }>(
      await client.get.api.web.tvshows.myList({ query: { limit, offset } })
    )

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getFavoritesFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })

  // POST /api/web/tvshows/my-list/{id}/add
  tvshows.base.addToFavoritesFx.use(async ({ id }) => {
    payload(await client.post.api.web.tvshows.myList(id).add())
  })

  // POST /api/web/tvshows/my-list/{id}/remove
  tvshows.base.removeFromFavoritesFx.use(async ({ id }) => {
    payload(await client.post.api.web.tvshows.myList(id).remove())
  })

  tvshows.base.getSeasonsWithEpisodesFx.use(async ({ id }) => {
    const getEpisodes = async ({
      seasonId,
      id,
    }: {
      seasonId: number
      id: number
    }) =>
      await tvshows.base.getTvShowEpisodesBySeasonIdFx({
        seasonId,
        id,
      })

    const seasons = await tvshows.base.getTvShowSeasonsByIdFx({
      id,
    })

    const promises = seasons.map(async (season) => {
      const episodes = await getEpisodes({ seasonId: season.id, id })
      return {
        ...season,
        episodes: [...episodes],
      }
    })

    return await Promise.all(promises)
  })

  // GET /api/web/tvshows/recommended
  tvshows.base.getRecommendedFx.use(async ({ limit, offset }) => {
    const data = payload<{ results: ApiTvShow[]; total: number }>(
      await client.get.api.web.tvshows.recommended({ query: { limit, offset } })
    )

    if (!data) {
      throw new Error('Empty answer in tvshows.base.getRecommendedFx')
    }

    const results = (data.results || []).map(format)

    return withTotal(results, data.total)
  })

  // GET /api/web/tvshows
  tvshows.base.getTvShowsFx.use(
    async ({
      limit,
      offset,
      by = _defaults.sortBy,
      order = _defaults.sortOrder,
    }) => {
      const data = payload<{ results: ApiTvShow[]; total: number }>(
        await client.get.api.web.tvshows({
          query: { limit, offset, sortBy: by, sortOrder: order },
        })
      )

      if (!data) {
        throw new Error('Empty answer in tvshows.base.getTvShowsFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )

  // GET /api/web/tvshows
  // GET /api/web/tvshows/by-genre/{genreId}
  // GET /api/web/tvshows/by-language/{languageId}
  tvshows.base.getTvShowsByFx.use(
    async ({ genreId, languageId, limit, offset, languageIdQueryParam }) => {
      const query = {
        limit,
        offset,
        languageId: languageIdQueryParam,
      }

      let result
      if (genreId) {
        result = await client.get.api.web.tvshows.byGenre(genreId, { query })
      } else if (languageId) {
        result = await client.get.api.web.tvshows.byLanguage(languageId, {
          query,
        })
      } else {
        result = await client.get.api.web.tvshows({ query })
      }

      const data = payload<{ results: ApiTvShow[]; total: number }>(result)

      if (!data) {
        throw new Error('Empty answer in tvshows.base.getTvShowsByFx')
      }

      const results = (data.results || []).map(format)

      return withTotal(results, data.total)
    }
  )
}
